// src/components/JarvislyComponents/PhoneInput.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import ValidatedInput from 'components/JarvislyComponents/ValidatedInput';
import { returnOnlyNumbers } from 'utils/helpers';
import { formatPhoneNumberByCountry } from 'utils/phoneFormatter';
import { getPhoneMeta } from 'utils/phone';
import countriesProfiles from 'assets/data/countriesProfiles.json';

// *******************************************************************************
// COMPATIBILITY EXPORTS (LEGACY API)
// *******************************************************************************
export const getPhoneProfileByNumber = (number, countryCode = 'BR') => {
  const { profile } = formatPhoneNumberByCountry(number, { countryCode });
  return profile;
};

export const getPhoneProfileByNumberMask = (value, countryCode = 'BR', profile) => {
  if (profile) return String(profile).toLowerCase();
  const { profile: auto } = formatPhoneNumberByCountry(value, { countryCode });
  return auto;
};

// *******************************************************************************
// HELPERS (INTERNOS)
// *******************************************************************************
const getCountryMeta = (cc = 'BR') => {
  const c = countriesProfiles
    // ?.filter(c => c.enabled)
    ?.find(x => x.countryCode === String(cc).toLowerCase());

  return (
    c || {
      countryCode: 'US',
      dial: '1',
      phoneMasks: {
        landline: '(999) 999-9999',
        mobile: '(999) 999-9999',
        free: '999-999-9999',
        robot: '999 999-9999',
      },
    }
  );
};

const stripLeadingParenDdd = masked => {
  if (!masked) return { maskedNoDdd: '', ddd: null };
  const m = String(masked);
  const re = /^\((\d+)\)\s*/;
  const match = m.match(re);
  if (match) {
    const ddd = match[1];
    return { maskedNoDdd: m.replace(re, ''), ddd };
  }
  return { maskedNoDdd: m, ddd: null };
};

/*
export const buildPhoneMeta = (rawDigits, countryCode, maskedFull, profileHint) => {
  const cc = String(countryCode || 'BR').toUpperCase();
  const country = getCountryMeta(cc);
  const ddi = `+${country.dial}`;
  const digitsNational = String(rawDigits || '');

  const { maskedNoDdd, ddd: dddFromMask } = stripLeadingParenDdd(maskedFull);

  let phoneNumberDdd = dddFromMask;
  if (!phoneNumberDdd) {
    if (cc === 'BR')
      phoneNumberDdd = digitsNational.length >= 10 ? digitsNational.slice(0, 2) : null;
    else if (cc === 'US')
      phoneNumberDdd = digitsNational.length >= 10 ? digitsNational.slice(0, 3) : null;
    else phoneNumberDdd = null;
  }

  let nationalWithoutDdd = digitsNational;
  if (phoneNumberDdd) {
    nationalWithoutDdd = digitsNational.slice(phoneNumberDdd.length);
  }

  let phoneCore = nationalWithoutDdd;
  if (cc === 'BR' && nationalWithoutDdd.length === 9 && nationalWithoutDdd.startsWith('9')) {
    phoneCore = nationalWithoutDdd.slice(1);
  }

  const fullPhoneNumber = `${ddi}${digitsNational}`;
  const maskedPhoneNumber = maskedFull || '';
  const maskedPhone = maskedNoDdd || maskedFull || '';

  return {
    countryCode: cc,
    fullPhoneNumber,
    phoneNumberDdi: ddi,
    phoneNumber: digitsNational,
    phoneNumberDdd: phoneNumberDdd,
    maskedPhoneNumber,
    phone: phoneCore,
    maskedPhone,
    profile: profileHint || undefined,
  };
};
*/

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PhoneInput = React.forwardRef(
  (
    {
      countryCode = 'BR',
      international = false,
      profile,
      status, // '', 'validating', 'success', 'error'  -> forwarded to ValidatedInput
      loading, // boolean -> forwarded to ValidatedInput
      ...restProps
    },
    ref
  ) => {
    const {
      onChange,
      prefix,
      suffix,
      onBlur,
      autoFocus,
      allowClear,
      disabled,
      addonAfter,
      addonBefore,
      label,
      styleItem,
      subdocumentField,
      required,
      noLabel,
      ...props
    } = restProps;

    const incoming = props?.value;
    let formattedFromValue = '';
    if (incoming && typeof incoming === 'object') {
      // When value is an object (from SimplePhone.handlePhoneChange), use 'national' for display
      // or format the raw phone digits if national is not available
      if (incoming.national) {
        formattedFromValue = incoming.national;
      } else {
        const rawPhone = incoming.phone || incoming.value || '';
        const { formatted } = formatPhoneNumberByCountry(rawPhone, { countryCode, profile });
        formattedFromValue = formatted;
      }
    } else {
      const { formatted } = formatPhoneNumberByCountry(incoming, { countryCode, profile });
      formattedFromValue = formatted;
    }

    // Extract raw phone for secondary formatting (when value is object)
    const rawPhoneValue = incoming && typeof incoming === 'object'
      ? (incoming.phone || incoming.value || '')
      : incoming;
    const { formatted } = formatPhoneNumberByCountry(rawPhoneValue, { countryCode, profile });

    const onLocalChange = ev => {
      const raw = ev?.target?.value || '';
      const numeric = returnOnlyNumbers(raw);

      const {
        maxLength,
        profile: autoProfile,
        formatted: formattedNow,
      } = formatPhoneNumberByCountry(numeric, { countryCode, profile });

      if (numeric?.length > maxLength) return;

      const effectiveProfile = (profile || autoProfile || '').toLowerCase();

      // Use getPhoneMeta from utils/phone.js for complete metadata including whatsappNumber
      const metaObj = getPhoneMeta(numeric, countryCode);

      onChange && onChange(ev, effectiveProfile, metaObj);
    };

    const valueForInput = formattedFromValue || formatted || '';

    return (
      <ValidatedInput
        {...props}
        ref={ref}
        value={valueForInput}
        onBlur={onBlur}
        autoFocus={autoFocus}
        prefix={prefix}
        suffix={suffix}
        addonBefore={addonBefore}
        addonAfter={addonAfter}
        // never pass allowClear to avoid AntD toggling suffix container
        disabled={disabled}
        onChange={onLocalChange}
        status={status}
        loading={loading}
      />
    );
  }
);

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PhoneInput;

// *******************************************************************************
// HELPERS
// *******************************************************************************
